/**
 * Uniform spatial hash grid for efficient neighbor queries.
 * Rebuilt from scratch each frame — cheaper than incremental updates
 * for fully-mobile swarm entities.
 */
export class SpatialGrid {
  private readonly cellSize: number;
  private readonly stride: number;
  private readonly cells = new Map<number, number[]>();

  /** Reusable result array to avoid per-query allocations */
  private readonly _queryResult: number[] = [];

  constructor(cellSize: number, mapWidth: number) {
    this.cellSize = cellSize;
    // stride must be large enough that no two rows share keys
    this.stride = Math.ceil(mapWidth / cellSize) + 1;
  }

  clear() {
    this.cells.clear();
  }

  insert(index: number, x: number, y: number) {
    const key = this.key(x, y);
    const bucket = this.cells.get(key);
    if (bucket) {
      bucket.push(index);
    } else {
      this.cells.set(key, [index]);
    }
  }

  /**
   * Returns indices of all entries within the cells overlapping the
   * query circle. Callers must do their own fine-grained distance check.
   * The returned array is reused between calls — do not hold a reference.
   */
  query(x: number, y: number, radius: number): number[] {
    const result = this._queryResult;
    result.length = 0;

    const cs = this.cellSize;
    const minCX = Math.floor((x - radius) / cs);
    const maxCX = Math.floor((x + radius) / cs);
    const minCY = Math.floor((y - radius) / cs);
    const maxCY = Math.floor((y + radius) / cs);

    for (let cy = minCY; cy <= maxCY; cy++) {
      for (let cx = minCX; cx <= maxCX; cx++) {
        const bucket = this.cells.get(cy * this.stride + cx);
        if (bucket) {
          for (let i = 0; i < bucket.length; i++) {
            result.push(bucket[i]!);
          }
        }
      }
    }

    return result;
  }

  private key(x: number, y: number): number {
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    return cy * this.stride + cx;
  }
}
